import { defineStore } from 'pinia';

import { fetchUsers, getTwitterAccount, getTwitterAccounts } from '@/apis/twitter';
import { logger } from '@/utils/logger';

export const useTwitterStore = defineStore('twitter', {
  resetOnBrandChange: true,
  state: () => ({
    // consider if state value should be cleared in resetState() action
    users: {},
    requests: {
      users: {},
      searches: {},
    },
    twitterAccount: null,
    twitterAccounts: null,
  }),
  getters: {
    getUser: (state) => (username) => state.users[username.toLowerCase()],
    twitterAccountsMap: (state) => {
      const result = Object.fromEntries(
        (state.twitterAccounts || []).map((account) => [account.brand_id, { ...account }]),
      );
      return result;
    },
  },
  actions: {
    resetState() {
      /*
     Reset state for events such as changing brands.
     Some state values such as twitterAccounts are reloaded but are not wanted to be nulled
     while fetching most recent account list.
      */
      this.users = {};
      this.requests = {
        users: {},
        searches: {},
      };
      this.twitterAccount = null;
    },
    addUsers(users) {
      users.forEach((user) => {
        this.users = {
          ...this.users,
          [user.username.toLowerCase()]: Object.freeze(user),
        };
      });
    },
    async searchUsers(query, brandId) {
      if (!this.requests.searches[query]) {
        const request = (async () => {
          const res = await fetchUsers({ handles: query, brandId });
          const users = res.data;
          this.addUsers(users);
          return users.map((user) => this.users[user.username.toLowerCase()]);
        })();
        this.requests.searches = {
          ...this.requests.searches,
          [query]: request,
        };
        request.catch(() => {
          const newValue = { ...this.requests.searches };
          delete newValue[query];
          this.requests.searches = newValue;
        });
      }
      return this.requests.searches[query];
    },
    async fetchUsers(usernames, brandId) {
      const lowercaseUsernames = usernames.map((username) => username.toLowerCase());
      const missingUsernames = lowercaseUsernames.filter(
        (username) => !this.requests.users[username] && !this.users[username],
      );
      if (missingUsernames.length > 0) {
        const request = fetchUsers({ handles: missingUsernames.join(','), brandId })
          .then((response) => this.addUsers(response.data))
          .catch(() =>
            lowercaseUsernames.forEach((username) => {
              const newValue = { ...this.requests.users };
              delete newValue[username];
              this.requests.users = newValue;
            }),
          );
        lowercaseUsernames.forEach((username) => {
          this.requests.users = {
            ...this.requests.users,
            [username]: request,
          };
        });
      }
      await Promise.all(lowercaseUsernames.map((username) => this.requests.users[username]));
    },
    async getTwitterAccount({ brandId }) {
      try {
        const response = await getTwitterAccount({ brandId });
        this.twitterAccount = response?.data;
      } catch (error) {
        if (error?.response?.status === 404) {
          this.twitterAccount = null;
        } else {
          throw error;
        }
      }
      return this.twitterAccount;
    },
    async getTwitterAccounts({ brandIds }) {
      try {
        const response = await getTwitterAccounts({ brandIds });
        this.twitterAccounts = response?.data;
      } catch (error) {
        if (error?.response?.status === 404) {
          this.twitterAccounts = null;
        } else if (error?.response?.status === 401) {
          this.twitterAccounts = null;
          logger.error(
            `twitterStore getTwitterAccounts request returned authentication error: ${error.response?.data?.description}`,
            {},
            error,
          );
        } else {
          throw error;
        }
      }
      return this.twitterAccounts;
    },
  },
});
